import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  CancelCrossIcon,
  Caption1,
  Header3,
  IconButton,
  Modal,
  Paper,
  SlideFromTop,
  Tooltip,
} from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { GroupByPicker } from "~/components/GroupByPicker/GroupByPicker";
import {
  getCurrencyCodeByProjectId,
  getDefaultCurrency,
  getFormatCurrencyArgs,
} from "~/store/selectors/selector.currencies";
import { CostSummaryCostItem } from "./CostSummaryCostItem";
import { CostSummaryLoader } from "./CostSummaryLoader";
import { CostSummaryTotalRow } from "./CostSummaryTotalRow";
import { ROW_CLASSES_ITEM, ROW_CLASSES_SHARED } from "./utils";
import DateStepperWithRange from "../DateStepper/DateStepperWithRange.jsx";

export const CostSummary = ({
  open,
  onClose,
  loading,
  costByType,
  projectId,
  expenses,
  nonBillableExpenses,
  totalBillable,
  totalNonBillable,
  totalInternal,
  withoutInternalRate,
  total,
  groupBy,
  onGroupByChange,
  onDateChange,
  showOptions,
  from,
  to,
  step,
  dataGroupBy,
}) => {
  const { t } = useTranslation();

  const filteredCostByType = costByType.filter((costData) => costData?.hours !== 0);

  const defaultCurrency = useSelector(getDefaultCurrency);
  const currencyCode = useSelector((state) => getCurrencyCodeByProjectId(state, projectId));
  const currencyArgs = useSelector((state) => getFormatCurrencyArgs(state, currencyCode));

  return (
    <Modal
      open={open}
      onClose={onClose}
    >
      <SlideFromTop in={open}>
        <Paper className="tw-overflow-auto tw-max-h-[80%] tw-max-w-[700px] tw-mx-2 sm:tw-mx-20 md:tw-mx-auto tw-mt-32">
          <div className="tw-w-[700px] tw-h-full">
            <div className="tw-flex tw-justify-between tw-items-center tw-py-5 tw-px-[30px] tw-border-0 tw-border-solid tw-border-b tw-border-border-primary">
              <Header3>{t("Cost Summary")}</Header3>
              <Tooltip title={t("Close")}>
                <IconButton
                  onClick={onClose}
                  variant="text gray"
                >
                  <CancelCrossIcon />
                </IconButton>
              </Tooltip>
            </div>
            {showOptions && (
              <div className="tw-flex tw-items-center tw-justify-between tw-pt-6 tw-pb-5 tw-mx-[30px] tw-border-0 tw-border-solid tw-border-b tw-border-border-primary">
                <div className="tw-flex tw-items-center">
                  <DateStepperWithRange
                    disabledSteps={["daily", "custom"]}
                    step={step}
                    from={from}
                    to={to}
                    onChange={onDateChange}
                  />
                </div>
                <div className="tw-flex tw-items-center">
                  <GroupByPicker
                    options={[
                      { id: "job_type", name: t("Job Type") },
                      { id: "user", name: t("People") },
                    ]}
                    selected={groupBy}
                    onChange={onGroupByChange}
                    forceCloseMenu
                    disableSearch
                    keepSameOptionsOrder
                    position="bottom-end"
                    disabledInternalSort
                  />
                </div>
              </div>
            )}
            <div className="tw-py-5 tw-px-[30px]">
              {loading ? (
                <CostSummaryLoader />
              ) : (
                <>
                  {!!filteredCostByType.length && (
                    <>
                      <div className={classNames(ROW_CLASSES_ITEM, ROW_CLASSES_SHARED)}>
                        <Caption1 weight="bold">{t("Time")}</Caption1>
                        <Caption1
                          weight="bold"
                          className="tw-truncate"
                        >
                          {dataGroupBy === "user" ? t("Name") : t("Job Type")}
                        </Caption1>
                        <Caption1
                          weight="bold"
                          align="right"
                        >
                          {t("Internal Cost")}
                        </Caption1>
                        <Caption1
                          weight="bold"
                          align="right"
                        >
                          {t("Spent")}
                        </Caption1>
                      </div>
                      {filteredCostByType.map((item, index) => (
                        <CostSummaryCostItem
                          key={index}
                          total={item.value}
                          internalCost={item.total_internal_value}
                          decimalSeparator={currencyArgs.decimal_separator}
                          decimalSpaces={currencyArgs.decimal_spaces}
                          thousandSeparator={currencyArgs.thousand_separator}
                          currencyCode={currencyCode}
                          defaultCurrency={defaultCurrency?.code}
                          hours={item.hours}
                          rate={item.rate}
                          id={item.id}
                          type={dataGroupBy}
                          withoutInternalRate={item.without_internal_rate}
                        />
                      ))}
                      <div className={classNames(ROW_CLASSES_ITEM, ROW_CLASSES_SHARED, "tw-h-8")} />
                    </>
                  )}
                  {!!expenses || !!nonBillableExpenses ? (
                    <>
                      <CostSummaryTotalRow
                        value={expenses}
                        label={`+${t("Expenses")}`}
                        decimalSeparator={currencyArgs.decimal_separator}
                        decimalSpaces={currencyArgs.decimal_spaces}
                        thousandSeparator={currencyArgs.thousand_separator}
                        currencyCode={currencyCode}
                      />
                      <CostSummaryTotalRow
                        value={nonBillableExpenses}
                        label={t("Expenses Non-Billable")}
                        decimalSeparator={currencyArgs.decimal_separator}
                        decimalSpaces={currencyArgs.decimal_spaces}
                        thousandSeparator={currencyArgs.thousand_separator}
                        currencyCode={currencyCode}
                      />
                      <div className={classNames(ROW_CLASSES_ITEM, ROW_CLASSES_SHARED, "tw-h-8")} />
                    </>
                  ) : null}

                  <CostSummaryTotalRow
                    value={totalBillable}
                    label={t("Total Billable")}
                    boldLabel
                    decimalSeparator={currencyArgs.decimal_separator}
                    decimalSpaces={currencyArgs.decimal_spaces}
                    thousandSeparator={currencyArgs.thousand_separator}
                    currencyCode={currencyCode}
                  />
                  <CostSummaryTotalRow
                    value={totalNonBillable}
                    label={t("Total Non-Billable")}
                    boldLabel
                    decimalSeparator={currencyArgs.decimal_separator}
                    decimalSpaces={currencyArgs.decimal_spaces}
                    thousandSeparator={currencyArgs.thousand_separator}
                    currencyCode={currencyCode}
                  />
                  <CostSummaryTotalRow
                    value={total}
                    label={t("Total")}
                    boldLabel
                    decimalSeparator={currencyArgs.decimal_separator}
                    decimalSpaces={currencyArgs.decimal_spaces}
                    thousandSeparator={currencyArgs.thousand_separator}
                    currencyCode={currencyCode}
                  />
                  <CostSummaryTotalRow
                    value={totalInternal}
                    label={t("Total Internal Cost")}
                    boldLabel
                    decimalSeparator={currencyArgs.decimal_separator}
                    decimalSpaces={currencyArgs.decimal_spaces}
                    thousandSeparator={currencyArgs.thousand_separator}
                    currencyCode={defaultCurrency?.code}
                    withoutInternalRate={withoutInternalRate}
                  />
                </>
              )}
            </div>
          </div>
        </Paper>
      </SlideFromTop>
    </Modal>
  );
};

CostSummary.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  loading: PropTypes.bool,
  costByType: PropTypes.array,
  projectId: PropTypes.number.isRequired,
  expenses: PropTypes.number,
  nonBillableExpenses: PropTypes.number,
  totalBillable: PropTypes.number,
  totalNonBillable: PropTypes.number,
  totalInternal: PropTypes.number,
  withoutInternalRate: PropTypes.bool,
  total: PropTypes.number,
  showOptions: PropTypes.bool,
  groupBy: PropTypes.string,
  onGroupByChange: PropTypes.func,
  onDateChange: PropTypes.func,
  step: PropTypes.oneOf(["weekly", "monthly", "quarterly", "yearly", "all"]),
  from: PropTypes.string,
  to: PropTypes.string,
  dataGroupBy: PropTypes.string,
};

CostSummary.defaultProps = {
  costByType: [],
  step: "all",
  groupBy: "job_type",
  dataGroupBy: "job",
};
